import { useRouter } from 'next/dist/client/router';
import React, { FC, useContext, useState } from 'react';
import { useMutation } from 'react-query';
import calculateLineItemQuantity from '../../../helpers/calculate-line-item-quantity';
import {
  DataLayerContext,
  DataLayerContextType,
} from '../../providers/data-layer/data-layer.provider';
import { BasketType } from '../../../models/basket-type';
import GtmEventNameEnum from '../../../models/gtm-event-name-enum';
import PlacementEnum from '../../../models/placement-enum';
import ProductTypeEnum from '../../../models/product-type-enum';
import { SubtotalDeductionsType } from '../../../models/subtotal-deductions-type';
import ButtonPrimary from '../../atoms/button-primary/button-primary';
import ButtonSecondary from '../../atoms/button-secondary/button-secondary';
import FieldCheckbox from '../../atoms/field-checkbox/field-checkbox';
import FieldText from '../../atoms/field-text/field-text';
import IcomoonIcon from '../../atoms/icomoon-icon/icomoon-icon';
import BasketBackOrderConfirmation from '../../molecules/basket-back-order-confirmation/basket-back-order-confirmation';
import replaceConfirmedBackOrderSkus from '../../providers/basket/actions/replace-confirmed-back-order-skus';
import {
  BasketDispatchContext,
  BasketStateContext,
} from '../../providers/basket/basket.provider';
import useAddPromoCode from '../../providers/basket/hooks/use-add-promo-code';
import useRemovePromoCode from '../../providers/basket/hooks/use-remove-promo-code';
import useUpdateMultipleLineItems from '../../providers/basket/hooks/use-update-multiple-line-items';
import close from '../../providers/overlay/close.overlay.actions';
import open from '../../providers/overlay/open.overlay.actions';
import { OverlayDispatchContext } from '../../providers/overlay/overlay.provider';
import useIsLoggedIn from '../../providers/user/hooks/use-is-logged-in';
import FreeDeliveryThreshold from '../../molecules/free-delivery-threshold/free-delivery-threshold';

interface Props extends SubtotalDeductionsType {
  readonly basket: BasketType;
}

const SubtotalDeductions: FC<Props> = ({
  addPromoCodeFieldLabel,
  addPromoCodeFieldNotValidError,
  applyButtonText,
  basket,
  checkoutButtonText,
  checkoutLink,
  excVatLabel,
  isScheduledCheckboxFieldLabel,
  outOfStockPopupAmendOrderButtonText,
  outOfStockPopupAmendOrderDescription,
  outOfStockPopupBackOrderButtonText,
  outOfStockPopupBackOrderDescription,
  outOfStockPopupDescription,
  outOfStockPopupHeading,
  outOfStockPopupInStockButtonText,
  outOfStockPopupInStockDescription,
  promoCodeAppliedLabel,
  removeLabel,
  subtotalLabel,
  totalLabel,
}) => {
  const { pushToDataLayer } =
    useContext<DataLayerContextType>(DataLayerContext);
  const router = useRouter();
  const basketState = useContext(BasketStateContext);
  const basketDispatch = useContext(BasketDispatchContext);
  const overlayDispatch = useContext(OverlayDispatchContext);
  const updateMultipleLineItems = useUpdateMultipleLineItems(undefined, {
    ignoreClearQuoteAlert: true,
  });
  const addPromoCode = useAddPromoCode();
  const removePromoCode = useRemovePromoCode();
  const isLoggedIn = useIsLoggedIn();
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [promoCode, setPromoCode] = useState<string>('');

  const addPromoCodeMutation = useMutation(
    async (code: string) => {
      await addPromoCode(code);
      pushToDataLayer(GtmEventNameEnum.BasketPromo, {
        ecommerce: {
          detail: {
            products: basketState.basket.lineItems.map((lineItem) => {
              return {
                price: lineItem.buyPriceFormatted,
                productImage: lineItem.product.properties.webImageLarge1425,
                productName: lineItem.product.name,
                productCode: lineItem.product.variantSku,
                masterCode: lineItem.product.sku,
                listPrice: lineItem.listPriceFormatted,
              };
            }),
          },
        },
        basketValue: basketState.total,
      });
    },
    {
      onError: () => {
        pushToDataLayer(GtmEventNameEnum.BasketPromoError, {
          errorMessage: addPromoCodeFieldNotValidError,
        });
      },
    }
  );

  const checkoutOrShowBackorderPreference = () => {
    const showBackorderPopup =
      basket.lineItems.filter((item) => {
        const quantity = calculateLineItemQuantity(item).total;

        return (
          item.quantityInStock < quantity &&
          !basketState.confirmedBackOrderSkus.includes(item.product.variantSku)
        );
      }).length > 0;

    if (showBackorderPopup && !isScheduled) {
      overlayDispatch(
        open(
          <BasketBackOrderConfirmation
            onAmend={() => {
              overlayDispatch(close());

              pushToDataLayer(GtmEventNameEnum.BasketBackorder);
            }}
            onContinueWithBackorder={() => {
              overlayDispatch(close());
              basketDispatch(
                replaceConfirmedBackOrderSkus(
                  basket.lineItems
                    .filter((item) => {
                      const quantity = calculateLineItemQuantity(item).total;

                      return item.quantityInStock < quantity;
                    })
                    .map((item) => item.product.variantSku)
                )
              );

              pushToDataLayer(GtmEventNameEnum.BasketBackorder);

              router.push(checkoutLink.url);
            }}
            onContinueWithClear={async () => {
              overlayDispatch(close());

              const newBasket = await updateMultipleLineItems(
                basket.lineItems
                  .map((item) => {
                    const quantity = calculateLineItemQuantity(item).total;

                    const missingQuantity = quantity - item.quantityInStock;

                    return missingQuantity > 0
                      ? {
                          orderLineId: item.orderLineId,
                          newQuantity: item.quantityInStock,
                        }
                      : null;
                  })
                  .filter((item) => !!item)
              );

              const removedItems = [];

              basket.lineItems.forEach((item) => {
                if (
                  !newBasket.lineItems
                    .map((newItem) => newItem.product.variantSku)
                    .includes(item.product.variantSku)
                ) {
                  removedItems.push(item);
                }
              });

              pushToDataLayer(GtmEventNameEnum.BasketBackorder);

              if (removedItems.length) {
                pushToDataLayer(GtmEventNameEnum.RemoveFromCart, {
                  ecommerce: {
                    remove: {
                      products: removedItems.map((item) => ({
                        dimension3: item.product.variantSku,
                        price: item.listPriceFormatted,
                        quantity: calculateLineItemQuantity(item).total,
                      })),
                    },
                  },
                  placement: PlacementEnum.BasketDetails,
                  productType: ProductTypeEnum.Website,
                });
              }

              router.push(checkoutLink.url);
            }}
            labels={{
              outOfStockPopupAmendOrderButtonText,
              outOfStockPopupAmendOrderDescription,
              outOfStockPopupBackOrderButtonText,
              outOfStockPopupBackOrderDescription,
              outOfStockPopupDescription,
              outOfStockPopupHeading,
              outOfStockPopupInStockButtonText,
              outOfStockPopupInStockDescription,
            }}
          />
        )
      );

      return;
    }

    if (isScheduled) {
      router.push(`${checkoutLink.url}?isScheduledOrder=true`);

      return;
    }

    router.push(checkoutLink.url);
  };

  if (!basket?.lineItems?.length) {
    return null;
  }

  return (
    <div className="py-component-spacing-mobile lg:py-component-spacing xl:pt-0 xl:pb-2">
      <div className="bg-light-blue flex flex-col space-y-4 pb-8 pt-6 px-6 rounded-lg">
        {!!basket.discountTotal && (
          <>
            <div className="flex justify-between border-grey-200 border-b pb-4">
              <p className="text-sm">{subtotalLabel}</p>

              <p className="text-sm">{basket.formattedSubTotal}</p>
            </div>

            <div className="flex justify-between border-grey-200 border-b pb-4">
              <div className="flex gap-2">
                <div className="w-4 h-4 bg-french-blue rounded-full flex items-center justify-center">
                  <IcomoonIcon color="white" name="check" size="xs" />
                </div>

                <div>
                  <p className="text-sm leading-none">
                    {promoCodeAppliedLabel}
                  </p>

                  <button
                    type="button"
                    className="text-sm text-french-blue underline"
                    onClick={async () => {
                      await removePromoCode();
                    }}
                  >
                    {removeLabel}
                  </button>
                </div>
              </div>

              <div className="text-sm leading-none">
                -{basket.formattedDiscountTotal}
              </div>
            </div>
          </>
        )}

        <div className="flex justify-between items-center  pb-3">
          <div className="grid">
            <span className="text-sm font-bold">{totalLabel}</span>

            <span className="text-sm">{excVatLabel}</span>
          </div>

          <span className="text-xl font-bold">{basket.formattedTotal}</span>
        </div>
        <FreeDeliveryThreshold
          spendLeftForFreeCarriage={basket?.spendLeftForFreeCarriage}
          spendLeftForFreeCarriageFormatted={
            basket?.spendLeftForFreeCarriageFormatted
          }
          subTotal={basket?.subTotal}
        />
        <div className="flex">
          <ButtonPrimary
            name={checkoutButtonText}
            onClick={() => checkoutOrShowBackorderPreference()}
            className="flex-grow"
          />
        </div>
        {!basket.discountTotal && (
          <div className="flex flex-col space-y-2">
            <p className="font-semibold">{addPromoCodeFieldLabel}</p>

            <div className="flex flex-col md:items-end md:flex-row space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex-grow">
                <FieldText
                  alwaysShowRing
                  backgroundColor="white"
                  disabled={isScheduled}
                  error={addPromoCodeMutation.isError && !isScheduled}
                  onChange={(value) => setPromoCode(value as string)}
                  value={promoCode}
                />
              </div>

              <ButtonSecondary
                className="flex-shrink-0"
                name={applyButtonText}
                paddingX="half"
                disabled={
                  promoCode?.length === 0 ||
                  addPromoCodeMutation.isLoading ||
                  isScheduled
                }
                onClick={async () => {
                  try {
                    await addPromoCodeMutation.mutateAsync(promoCode);
                    setPromoCode('');
                  } catch {
                    // Do nothing
                  }
                }}
              />
            </div>

            {addPromoCodeMutation.isError && !isScheduled && (
              <p className="mt-1 text-sm text-invalid">
                {addPromoCodeFieldNotValidError}
              </p>
            )}
          </div>
        )}

        {isLoggedIn && (
          <div className="py-2">
            <FieldCheckbox
              backgroundColor="bg-white"
              label={isScheduledCheckboxFieldLabel}
              onChange={async (value: boolean) => {
                setIsScheduled(value);

                if (value) {
                  setPromoCode('');
                  await removePromoCode();
                }
              }}
              value={isScheduled}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SubtotalDeductions;
